import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../App";
import { Search, ChevronLeft, ChevronRight, Eye, Trash2, Clock, CheckCircle, XCircle, Users, AlertTriangle, Save, Pencil, ChevronDown, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { authAxios } from "../utils/auth";
import AdminMenu from "../components/AdminMenu";

const ALL_STATUSES = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "spam", label: "Spam" },
];

const STATUS_ICONS = {
  pending: <Clock className="w-3.5 h-3.5" />,
  approved: <CheckCircle className="w-3.5 h-3.5" />,
  rejected: <XCircle className="w-3.5 h-3.5" />,
  spam: <AlertTriangle className="w-3.5 h-3.5" />,
};

const STATUS_COLORS = {
  pending: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  rejected: "bg-red-500/15 text-red-400 border-red-500/30",
  spam: "bg-orange-500/15 text-orange-400 border-orange-500/30",
};

const INTERESTS_MAP = {
  romantic: "Romantic Coaching", dating: "Next Level Dating", family: "Family & Friendship",
  professional: "Professional", neurodivergent: "Neurodivergent Tools",
  simulation: "Social Skills Sim", matchmaking: "Deep Matchmaking", all: "All of the Above",
};

export default function WaitlistPage() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCyber = theme === "agent_eros_1";
  const accent = isCyber ? "text-cyan-400" : "text-divine-gold";
  const accentBorder = isCyber ? "border-cyan-500/30" : "border-divine-gold/30";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({});

  // Selection
  const [selected, setSelected] = useState(new Set());
  const [showBulkMenu, setShowBulkMenu] = useState(false);

  // Detail modal
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: "20" });
      if (search.trim()) params.set("search", search.trim());
      if (statusFilter) params.set("status", statusFilter);
      if (verifiedFilter) params.set("verified", verifiedFilter);
      const res = await authAxios.get(`/waitlist/admin/list?${params}`);
      setItems(res.data.items);
      setTotal(res.data.total);
      setPages(res.data.pages);
      if (res.data.counts) setCounts(res.data.counts);
    } catch (err) {
      console.error("Fetch waitlist error:", err);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, verifiedFilter]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { setPage(1); }, [search, statusFilter, verifiedFilter]);

  // Clear selection when page/filter changes
  useEffect(() => { setSelected(new Set()); }, [page, search, statusFilter, verifiedFilter]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((i) => i.id)));
    }
  };

  const openDetail = async (item) => {
    setSelectedItem(item);
    setEditing(false);
    setShowModal(true);
    // Fetch enriched detail (includes ref_affiliate_name)
    if (item.ref_affiliate) {
      try {
        const res = await authAxios.get(`/waitlist/admin/${item.id}`);
        setSelectedItem(res.data);
      } catch (e) { /* fallback to list data */ }
    }
  };

  const startEditing = () => {
    setEditData({
      name: selectedItem.name,
      email: selectedItem.email,
      phone: selectedItem.phone || "",
      country: selectedItem.country || "",
      about: selectedItem.about || "",
      status: selectedItem.status,
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await authAxios.put(`/waitlist/admin/${selectedItem.id}`, editData);
      const updated = res.data;
      setSelectedItem(updated);
      setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      setEditing(false);
    } catch (err) {
      console.error("Save error:", err);
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await authAxios.patch(`/waitlist/admin/${id}`, { status: newStatus });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status: newStatus } : i)));
      if (selectedItem?.id === id) setSelectedItem((prev) => ({ ...prev, status: newStatus }));
    } catch (err) {
      console.error("Update status error:", err);
    }
  };

  const deleteSignup = async (id) => {
    if (!window.confirm("Delete this signup permanently?")) return;
    try {
      await authAxios.delete(`/waitlist/admin/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setTotal((prev) => prev - 1);
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      if (selectedItem?.id === id) setShowModal(false);
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const resendVerification = async (id) => {
    setResending(true);
    try {
      const res = await authAxios.post(`/waitlist/admin/${id}/resend-verification`);
      alert(res.data?.message || "Verification email resent");
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to resend");
    } finally {
      setResending(false);
    }
  };

  // Bulk actions
  const bulkSetStatus = async (newStatus) => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      await authAxios.post("/waitlist/admin/bulk-status", { ids, status: newStatus });
      setItems((prev) => prev.map((i) => (ids.includes(i.id) ? { ...i, status: newStatus } : i)));
      setSelected(new Set());
      setShowBulkMenu(false);
      fetchList();
    } catch (err) {
      console.error("Bulk status error:", err);
    }
  };

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length || !window.confirm(`Delete ${ids.length} signup(s) permanently?`)) return;
    try {
      await authAxios.post("/waitlist/admin/bulk-delete", { ids });
      setSelected(new Set());
      fetchList();
    } catch (err) {
      console.error("Bulk delete error:", err);
    }
  };

  if (user?.role !== "admin") {
    return <div className="min-h-screen bg-divine-black flex items-center justify-center text-white/50">Admin access required</div>;
  }

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }) + " " + d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
  };

  const allSelected = items.length > 0 && selected.size === items.length;

  return (
    <div className="min-h-screen bg-divine-black text-white p-6" data-testid="waitlist-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Users className={`w-6 h-6 ${accent}`} />
          <h1 className="text-2xl font-cinzel" data-testid="waitlist-title">Beta Waitlist</h1>
          <span className="ml-auto text-white/40 text-sm">{total} total signups</span>
          <Button variant="outline" size="sm" onClick={() => navigate("/mailer")} className="border-white/10 text-white/60 hover:text-white gap-1.5" data-testid="goto-mailer-btn">
            <Mail className="w-4 h-4" /> Mailer
          </Button>
          <AdminMenu />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              placeholder="Search name, email, country, IP..."
              className="pl-10 bg-divine-black-light border-white/10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="waitlist-search"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {ALL_STATUSES.map((s) => (
              <Button
                key={s.value}
                variant={statusFilter === s.value ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter(s.value)}
                className={statusFilter === s.value ? "" : "border-white/10 text-white/50 hover:text-white"}
                data-testid={`filter-${s.value || "all"}`}
              >
                {s.label}
                {s.value && counts[s.value] !== undefined && (
                  <span className="ml-1.5 text-xs opacity-60">({counts[s.value]})</span>
                )}
              </Button>
            ))}
            <span className="w-px bg-white/10 mx-1" />
            <Button
              variant={verifiedFilter === "true" ? "default" : "outline"}
              size="sm"
              onClick={() => setVerifiedFilter(verifiedFilter === "true" ? "" : "true")}
              className={verifiedFilter === "true" ? "bg-emerald-600 hover:bg-emerald-700" : "border-emerald-500/30 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"}
              data-testid="filter-verified"
            >
              <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
              Verified
              {counts.verified !== undefined && (
                <span className="ml-1.5 text-xs opacity-60">({counts.verified})</span>
              )}
            </Button>
          </div>
        </div>

        {/* Bulk Action Bar */}
        {selected.size > 0 && (
          <div className="flex items-center gap-3 mb-4 p-3 rounded-lg bg-white/5 border border-white/10" data-testid="bulk-action-bar">
            <span className="text-sm text-white/70">{selected.size} selected</span>
            <div className="relative">
              <Button variant="outline" size="sm" onClick={() => setShowBulkMenu(!showBulkMenu)} className="border-white/10" data-testid="bulk-status-btn">
                Set Status <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
              </Button>
              {showBulkMenu && (
                <div className="absolute top-full left-0 mt-1 bg-divine-black-light border border-white/10 rounded-lg shadow-xl z-20 w-40 py-1" onClick={() => setShowBulkMenu(false)}>
                  {["pending", "approved", "rejected", "spam"].map((s) => (
                    <button key={s} onClick={() => bulkSetStatus(s)} className="w-full text-left px-4 py-2 text-sm text-white/70 hover:bg-white/5 hover:text-white flex items-center gap-2 capitalize" data-testid={`bulk-set-${s}`}>
                      {STATUS_ICONS[s]} {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={bulkDelete} className="border-red-500/30 text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid="bulk-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} className="text-white/40 ml-auto">Clear</Button>
          </div>
        )}

        {/* Table */}
        <div className={`border ${accentBorder} rounded-lg overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="waitlist-table">
              <thead>
                <tr className="border-b border-white/10 bg-white/5">
                  <th className="px-3 py-3 w-10">
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="rounded border-white/30 bg-transparent cursor-pointer" data-testid="select-all-checkbox" />
                  </th>
                  <th className="text-left px-4 py-3 text-white/50 font-medium">Name</th>
                  <th className="text-left px-4 py-3 text-white/50 font-medium">Email</th>
                  <th className="text-left px-4 py-3 text-white/50 font-medium hidden md:table-cell">Country</th>
                  <th className="text-left px-4 py-3 text-white/50 font-medium hidden xl:table-cell">IP Origin</th>
                  <th className="text-left px-4 py-3 text-white/50 font-medium">Status</th>
                  <th className="text-left px-4 py-3 text-white/50 font-medium hidden sm:table-cell">Date</th>
                  <th className="text-right px-4 py-3 text-white/50 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="8" className="px-4 py-12 text-center text-white/30">Loading...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan="8" className="px-4 py-12 text-center text-white/30">No signups found</td></tr>
                ) : items.map((item) => (
                  <tr key={item.id} className={`border-b border-white/5 hover:bg-white/5 transition-colors ${item.status === "spam" ? "opacity-60" : ""}`} data-testid={`waitlist-row-${item.id}`}>
                    <td className="px-3 py-3">
                      <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggleSelect(item.id)} className="rounded border-white/30 bg-transparent cursor-pointer" data-testid={`select-${item.id}`} />
                    </td>
                    <td className="px-4 py-3 text-white/90">{item.name}</td>
                    <td className="px-4 py-3 text-white/60">
                      <span className="inline-flex items-center gap-1.5">
                        {item.email}
                        {item.email_verified && <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0" data-testid={`verified-tick-${item.id}`} />}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/50 hidden md:table-cell">{item.country || "-"}</td>
                    <td className="px-4 py-3 text-white/40 text-xs hidden xl:table-cell">
                      {item.ip_country ? <span title={item.ip}>{item.ip_country}</span> : item.ip || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs border ${STATUS_COLORS[item.status] || STATUS_COLORS.pending}`}>
                        {STATUS_ICONS[item.status] || STATUS_ICONS.pending}
                        {item.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/40 text-xs hidden sm:table-cell">{formatDate(item.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openDetail(item)} className="h-8 w-8 p-0" data-testid={`view-${item.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteSignup(item.id)} className="h-8 w-8 p-0 text-red-400 hover:text-red-300" data-testid={`delete-${item.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between mt-4" data-testid="waitlist-pagination">
            <span className="text-white/40 text-sm">Page {page} of {pages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="border-white/10">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} className="border-white/10">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showModal && selectedItem && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) { setShowModal(false); setEditing(false); } }} data-testid="waitlist-detail-modal">
          <div className={`w-full max-w-lg bg-divine-black-light border ${accentBorder} rounded-lg p-6 max-h-[85vh] overflow-y-auto`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-cinzel">{editing ? "Edit Signup" : "Signup Detail"}</h2>
              <div className="flex items-center gap-1">
                {!editing && (
                  <Button variant="ghost" size="sm" onClick={startEditing} className="h-8 w-8 p-0" data-testid="edit-detail-btn">
                    <Pencil className="w-4 h-4" />
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => { setShowModal(false); setEditing(false); }} className="h-8 w-8 p-0" data-testid="close-detail-modal">
                  <XCircle className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {editing ? (
              <div className="space-y-4" data-testid="edit-form">
                <EditField label="Name" value={editData.name} onChange={(v) => setEditData({ ...editData, name: v })} testId="edit-name" />
                <EditField label="Email" value={editData.email} onChange={(v) => setEditData({ ...editData, email: v })} testId="edit-email" />
                <EditField label="Phone" value={editData.phone} onChange={(v) => setEditData({ ...editData, phone: v })} testId="edit-phone" />
                <EditField label="Country" value={editData.country} onChange={(v) => setEditData({ ...editData, country: v })} testId="edit-country" />
                <div>
                  <span className="text-white/40 text-xs uppercase tracking-wider block mb-1">About</span>
                  <textarea className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm text-white/80 resize-vertical" rows="3" value={editData.about} onChange={(e) => setEditData({ ...editData, about: e.target.value })} data-testid="edit-about" />
                </div>
                <div>
                  <span className="text-white/40 text-xs uppercase tracking-wider block mb-2">Status</span>
                  <div className="flex gap-2 flex-wrap">
                    {["pending", "approved", "rejected", "spam"].map((s) => (
                      <Button key={s} variant={editData.status === s ? "default" : "outline"} size="sm" onClick={() => setEditData({ ...editData, status: s })} className={editData.status === s ? "" : "border-white/10 text-white/50"} data-testid={`edit-status-${s}`}>
                        {STATUS_ICONS[s]} <span className="ml-1.5 capitalize">{s}</span>
                      </Button>
                    ))}
                  </div>
                </div>
                {/* Read-only fields */}
                {selectedItem.ref_affiliate && (
                  <div data-testid="edit-ref-affiliate">
                    <span className="text-white/40 text-xs uppercase tracking-wider block mb-0.5">Affiliate Code</span>
                    <p className="text-white/80 text-sm">
                      {selectedItem.ref_affiliate}
                      {selectedItem.ref_affiliate_name && <span className="text-white/50"> — {selectedItem.ref_affiliate_name}</span>}
                    </p>
                  </div>
                )}
                <ReadField label="IP Address" value={selectedItem.ip || "-"} />
                <ReadField label="IP Country" value={selectedItem.ip_country || "-"} />
                <ReadField label="Interests" value={selectedItem.interests?.length ? selectedItem.interests.map((i) => INTERESTS_MAP[i] || i).join(", ") : "-"} />
                {selectedItem.spam_flags?.length > 0 && (
                  <div>
                    <span className="text-orange-400 text-xs uppercase tracking-wider block mb-1">Spam Flags</span>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedItem.spam_flags.map((f) => (
                        <span key={f} className="px-2 py-0.5 text-xs bg-orange-500/15 text-orange-400 border border-orange-500/30 rounded-full">{f}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
                  <Button size="sm" onClick={saveEdit} disabled={saving} data-testid="save-edit-btn">
                    <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Changes"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setEditing(false)} className="border-white/10 text-white/50">Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <ReadField label="Name" value={selectedItem.name} />
                <ReadField label="Email" value={selectedItem.email} />
                <ReadField label="Phone" value={selectedItem.phone || "-"} />
                <ReadField label="Country" value={selectedItem.country || "-"} />
                {selectedItem.ref_affiliate && (
                  <div data-testid="detail-ref-affiliate">
                    <span className="text-white/40 text-xs uppercase tracking-wider block mb-0.5">Affiliate Code</span>
                    <p className="text-white/80 text-sm">
                      {selectedItem.ref_affiliate}
                      {selectedItem.ref_affiliate_name && <span className="text-white/50"> — {selectedItem.ref_affiliate_name}</span>}
                    </p>
                  </div>
                )}
                <ReadField label="IP Address" value={selectedItem.ip || "-"} />
                <ReadField label="IP Country" value={selectedItem.ip_country || "-"} />
                <ReadField label="Interests" value={selectedItem.interests?.length ? selectedItem.interests.map((i) => INTERESTS_MAP[i] || i).join(", ") : "-"} />
                <div>
                  <span className="text-white/40 text-xs uppercase tracking-wider block mb-1">About</span>
                  <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">{selectedItem.about || "-"}</p>
                </div>
                <ReadField label="Submitted" value={formatDate(selectedItem.created_at)} />
                {selectedItem.updated_at && <ReadField label="Last Updated" value={formatDate(selectedItem.updated_at)} />}
                {selectedItem.spam_flags?.length > 0 && (
                  <div>
                    <span className="text-orange-400 text-xs uppercase tracking-wider block mb-1">Spam Flags</span>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedItem.spam_flags.map((f) => (
                        <span key={f} className="px-2 py-0.5 text-xs bg-orange-500/15 text-orange-400 border border-orange-500/30 rounded-full">{f}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <span className="text-white/40 text-xs uppercase tracking-wider block mb-2">Status</span>
                  <div className="flex gap-2 flex-wrap">
                    {["pending", "approved", "rejected", "spam"].map((s) => (
                      <Button key={s} variant={selectedItem.status === s ? "default" : "outline"} size="sm" onClick={() => updateStatus(selectedItem.id, s)} className={selectedItem.status === s ? (s === "approved" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "") : (s === "approved" ? "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/15" : "border-white/10 text-white/50")} data-testid={`status-btn-${s}`}>
                        {STATUS_ICONS[s]} <span className="ml-1.5 capitalize">{s}</span>
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="flex justify-between mt-6 pt-4 border-t border-white/10">
                  {!selectedItem.email_verified && (
                    <Button variant="outline" size="sm" className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10" onClick={() => resendVerification(selectedItem.id)} disabled={resending} data-testid="resend-verification-btn">
                      <Mail className="w-4 h-4 mr-2" /> {resending ? "Sending..." : "Resend Verification"}
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 ml-auto" onClick={() => deleteSignup(selectedItem.id)} data-testid="modal-delete-btn">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Signup
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ReadField({ label, value }) {
  return (
    <div>
      <span className="text-white/40 text-xs uppercase tracking-wider block mb-0.5">{label}</span>
      <p className="text-white/80 text-sm">{value}</p>
    </div>
  );
}

function EditField({ label, value, onChange, testId }) {
  return (
    <div>
      <span className="text-white/40 text-xs uppercase tracking-wider block mb-1">{label}</span>
      <input className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 text-sm text-white/80" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}
